import React, { useMemo, useState } from 'react';
import {
  IonAlert,
  IonButton,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonTextarea,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import { addOutline, businessOutline, storefrontOutline, trashOutline } from 'ionicons/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateWarehouseDto,
  IMaterialWarehouseStock,
  IWarehouse,
  WarehouseStatus,
} from 'shared';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { useLanguage } from '../context/LanguageContext';
import apiClient from '../api/client';
import MetricCard from '../components/MetricCard';
import { formatCurrency, formatDecimal } from '../utils/format';
import { RefresherEvent } from '../types/ionic';

interface WarehouseFormState {
  id?: string;
  name: string;
  location: string;
  description: string;
  status: WarehouseStatus;
}

const defaultState: WarehouseFormState = {
  name: '',
  location: '',
  description: '',
  status: WarehouseStatus.ACTIVE,
};

const Warehouses: React.FC = () => {
  const { t, language } = useLanguage();
  const locale = language === 'vi' ? 'vi-VN' : 'en-US';
  const queryClient = useQueryClient();
  const [showFormModal, setShowFormModal] = useState(false);
  const [formState, setFormState] = useState<WarehouseFormState>(defaultState);
  const [selectedWarehouse, setSelectedWarehouse] = useState<IWarehouse | null>(null);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  const warehousesQuery = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => {
      const { data } = await apiClient.get<IWarehouse[]>('/warehouses');
      return data;
    },
  });

  const warehouseStockQuery = useQuery({
    queryKey: ['warehouse-stock', selectedWarehouse?.id],
    queryFn: async () => {
      if (!selectedWarehouse?.id) return [];
      const { data } = await apiClient.get<IMaterialWarehouseStock[]>(
        `/warehouses/${selectedWarehouse.id}/stock`,
      );
      return data;
    },
    enabled: Boolean(selectedWarehouse?.id),
  });

  const [presentToast] = useIonToast();

  const createWarehouseMutation = useMutation({
    mutationFn: async (payload: CreateWarehouseDto) => {
      const { data } = await apiClient.post<IWarehouse>('/warehouses', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      presentToast({
        message: t('warehouseCreated') ?? 'Warehouse created',
        duration: 2000,
        color: 'success',
      });
      setShowFormModal(false);
      setFormState(defaultState);
    },
    onError: () => {
      presentToast({
        message: t('warehouseCreateFailed') ?? 'Failed to create warehouse',
        duration: 2000,
        color: 'danger',
      });
    },
  });

  const updateWarehouseMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateWarehouseDto> }) => {
      const { data: response } = await apiClient.put<IWarehouse>(`/warehouses/${id}`, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      if (formState.id) {
        queryClient.invalidateQueries({ queryKey: ['warehouse-stock', formState.id] });
      }
      setShowFormModal(false);
      setFormState(defaultState);
      presentToast({
        message: t('warehouseUpdated') ?? 'Warehouse updated',
        duration: 2000,
        color: 'success',
      });
    },
    onError: () => {
      presentToast({
        message: t('warehouseUpdateFailed') ?? 'Failed to update warehouse',
        duration: 2000,
        color: 'danger',
      });
    },
  });

  const deleteWarehouseMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/warehouses/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      setSelectedWarehouse(null);
      presentToast({
        message: t('warehouseDeleted') ?? 'Warehouse deleted',
        duration: 2000,
        color: 'success',
      });
    },
    onError: (error: unknown) => {
      const messageText =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      presentToast({
        message: messageText || t('warehouseDeleteFailed') || 'Failed to delete warehouse',
        duration: 2500,
        color: 'danger',
      });
    },
  });

  const handleRefresh = async (event: RefresherEvent) => {
    try {
      await Promise.all([warehousesQuery.refetch(), warehouseStockQuery.refetch()]);
    } finally {
      event.detail.complete();
    }
  };

  const openCreateModal = () => {
    setMode('create');
    setFormState(defaultState);
    setShowFormModal(true);
  };

  const openEditModal = (warehouse: IWarehouse) => {
    setMode('edit');
    setFormState({
      id: warehouse.id,
      name: warehouse.name,
      location: warehouse.location ?? '',
      description: warehouse.description ?? '',
      status: warehouse.status ?? WarehouseStatus.ACTIVE,
    });
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (createWarehouseMutation.isPending || updateWarehouseMutation.isPending) {
      return;
    }
    setShowFormModal(false);
    setFormState(defaultState);
  };

  const submitForm = () => {
    if (!formState.name.trim()) {
      presentToast({
        message: t('enterWarehouseName') ?? 'Enter name',
        duration: 2000,
        color: 'warning',
      });
      return;
    }

    const payload: CreateWarehouseDto = {
      name: formState.name.trim(),
      location: formState.location.trim() || undefined,
      description: formState.description.trim() || undefined,
      status: formState.status,
    };

    if (mode === 'create') {
      createWarehouseMutation.mutate(payload);
    } else if (formState.id) {
      updateWarehouseMutation.mutate({ id: formState.id, data: payload });
    }
  };

  const warehouseMetrics = useMemo(() => {
    const data = warehousesQuery.data ?? [];
    const totalWarehouses = data.length;
    const activeWarehouses = data.filter((w) => w.status === WarehouseStatus.ACTIVE).length;
    const inactiveWarehouses = totalWarehouses - activeWarehouses;
    const stockValue = (warehouseStockQuery.data ?? []).reduce((sum, stock) => {
      const cost = Number(stock.rawMaterial?.costPerUnit ?? 0);
      return sum + Number(stock.quantity || 0) * cost;
    }, 0);
    return { totalWarehouses, activeWarehouses, inactiveWarehouses, stockValue };
  }, [warehousesQuery.data, warehouseStockQuery.data]);

  const stocks = useMemo(() => {
    return (warehouseStockQuery.data ?? []).map((stock) => {
      const materialName = stock.rawMaterial?.name || t('unknown');
      const quantity = Number(stock.quantity || 0);
      const unit = stock.rawMaterial?.unit || '';
      const cost = Number(stock.rawMaterial?.costPerUnit || 0);
      return {
        id: stock.id,
        name: materialName,
        quantity,
        unit,
        value: quantity * cost,
        minStock: Number(stock.rawMaterial?.minStockLevel ?? 0),
      };
    });
  }, [warehouseStockQuery.data, t]);

  const handleSelectWarehouse = (warehouse: IWarehouse) => {
    setSelectedWarehouse(warehouse);
  };

  const warehouses = warehousesQuery.data ?? [];

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonTitle>{t('warehouses')}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent pullingText="Pull to refresh" />
        </IonRefresher>

        <div style={{ paddingTop: 'var(--ion-safe-area-top)' }}>
          <section style={{ marginBottom: 16 }}>
            <MetricCard
              title={t('totalWarehouses') ?? 'Warehouses'}
              value={`${warehouseMetrics.totalWarehouses}`}
              context={`${t('active') ?? 'Active'}: ${warehouseMetrics.activeWarehouses}`}
              accent="primary"
            />
            <MetricCard
              title={t('inactiveWarehouses') ?? 'Inactive'}
              value={`${warehouseMetrics.inactiveWarehouses}`}
              context={t('warehouseStatusSummary') ?? 'Status overview'}
              accent="warning"
            />
            <MetricCard
              title={t('stockValue')}
              value={formatCurrency(warehouseMetrics.stockValue, locale)}
              context={t('inventoryOverview') ?? 'Inventory overview'}
              accent="success"
            />
          </section>

          <IonButton
            expand="block"
            color="primary"
            style={{ marginBottom: 16 }}
            onClick={openCreateModal}
          >
            <IonIcon icon={addOutline} slot="start" />
            {t('addWarehouse') ?? 'Add warehouse'}
          </IonButton>

          {warehousesQuery.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
              <IonSpinner />
            </div>
          ) : warehouses.length === 0 ? (
            <IonText color="medium">
              <p>{t('noData')}</p>
            </IonText>
          ) : (
            <IonList>
              {warehouses.map((warehouse) => (
                <IonItem
                  key={warehouse.id}
                  button
                  detail
                  onClick={() => handleSelectWarehouse(warehouse)}
                >
                  <IonIcon slot="start" icon={storefrontOutline} color="primary" />
                  <IonLabel>
                    <h2 style={{ fontWeight: 600 }}>{warehouse.name}</h2>
                    <p>{warehouse.location || t('locationUnknown') || '—'}</p>
                    <p>
                      {t('status')}: {warehouse.status === WarehouseStatus.ACTIVE ? t('active') : t('inactive')}
                    </p>
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>
          )}
        </div>

        <IonModal isOpen={showFormModal} onDidDismiss={closeFormModal}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>
                {mode === 'create' ? t('addWarehouse') ?? 'Add warehouse' : t('editWarehouse') ?? 'Edit warehouse'}
              </IonTitle>
              <IonButton slot="end" fill="clear" onClick={closeFormModal}>
                {t('close')}
              </IonButton>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonList>
              <IonItem>
                <IonLabel position="stacked">{t('name')}</IonLabel>
                <IonInput
                  value={formState.name}
                  onIonChange={(event) => setFormState((prev) => ({ ...prev, name: event.detail.value ?? '' }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">{t('location')}</IonLabel>
                <IonInput
                  value={formState.location}
                  onIonChange={(event) => setFormState((prev) => ({ ...prev, location: event.detail.value ?? '' }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">{t('description')}</IonLabel>
                <IonTextarea
                  value={formState.description}
                  onIonChange={(event) => setFormState((prev) => ({ ...prev, description: event.detail.value ?? '' }))}
                  autoGrow
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">{t('status')}</IonLabel>
                <IonSelect
                  value={formState.status}
                  onIonChange={(event) =>
                    setFormState((prev) => ({ ...prev, status: (event.detail.value as WarehouseStatus) ?? WarehouseStatus.ACTIVE }))
                  }
                >
                  <IonSelectOption value={WarehouseStatus.ACTIVE}>{t('active')}</IonSelectOption>
                  <IonSelectOption value={WarehouseStatus.INACTIVE}>{t('inactive')}</IonSelectOption>
                </IonSelect>
              </IonItem>
            </IonList>
            <IonButton
              expand="block"
              style={{ marginTop: 16 }}
              onClick={submitForm}
              disabled={createWarehouseMutation.isPending || updateWarehouseMutation.isPending}
            >
              {mode === 'create' ? t('save') : t('update') ?? 'Update'}
            </IonButton>
          </IonContent>
        </IonModal>

        <IonModal
          isOpen={Boolean(selectedWarehouse)}
          onDidDismiss={() => setSelectedWarehouse(null)}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>{selectedWarehouse?.name ?? t('warehouse')}</IonTitle>
              <IonButton slot="end" fill="clear" onClick={() => setSelectedWarehouse(null)}>
                {t('close')}
              </IonButton>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {selectedWarehouse ? (
              <>
                <section style={{ marginBottom: 16 }}>
                  <IonText color="medium">
                    <p>{selectedWarehouse.location || t('locationUnknown') || '—'}</p>
                    {selectedWarehouse.description ? <p>{selectedWarehouse.description}</p> : null}
                  </IonText>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <IonButton size="small" onClick={() => openEditModal(selectedWarehouse)}>
                      <IonIcon slot="start" icon={businessOutline} />
                      {t('edit')}
                    </IonButton>
                    <IonButton
                      size="small"
                      color="danger"
                      onClick={() => setShowDeleteAlert(true)}
                      disabled={deleteWarehouseMutation.isPending}
                    >
                      <IonIcon slot="start" icon={trashOutline} />
                      {t('delete')}
                    </IonButton>
                  </div>
                </section>

                <section>
                  <IonText color="primary">
                    <h2 style={{ marginBottom: 12 }}>{t('inventory')}</h2>
                  </IonText>
                  {warehouseStockQuery.isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                      <IonSpinner />
                    </div>
                  ) : stocks.length === 0 ? (
                    <IonText color="medium">
                      <p>{t('noData')}</p>
                    </IonText>
                  ) : (
                    <IonList>
                      {stocks.map((stock) => (
                        <IonItem key={stock.id}>
                          <IonLabel>
                            <h3 style={{ fontWeight: 600 }}>{stock.name}</h3>
                            <p>
                              {formatDecimal(stock.quantity, { locale, maximumFractionDigits: 2 })} {stock.unit} • {t('minStockLevel')}: {formatDecimal(stock.minStock, { locale, maximumFractionDigits: 2 })} {stock.unit}
                            </p>
                          </IonLabel>
                          <IonText slot="end" color={stock.quantity < stock.minStock ? 'danger' : 'success'}>
                            <strong>{formatCurrency(stock.value, locale)}</strong>
                          </IonText>
                        </IonItem>
                      ))}
                    </IonList>
                  )}
                </section>
              </>
            ) : null}
          </IonContent>
        </IonModal>

        <IonAlert
          isOpen={showDeleteAlert}
          header={t('confirmDeleteWarehouse') ?? 'Delete warehouse?'}
          message={t('deleteWarehouseMessage') ?? 'Deleting a warehouse removes it permanently.'}
          buttons={[
            {
              text: t('cancel'),
              role: 'cancel',
              handler: () => setShowDeleteAlert(false),
            },
            {
              text: t('delete'),
              role: 'destructive',
              handler: () => {
                if (selectedWarehouse) {
                  deleteWarehouseMutation.mutate(selectedWarehouse.id);
                }
                setShowDeleteAlert(false);
                setSelectedWarehouse(null);
              },
            },
          ]}
          onDidDismiss={() => setShowDeleteAlert(false)}
        />
      </IonContent>
    </IonPage>
  );
};

export default Warehouses;
